import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { AxiosError } from 'axios';
import { useTranslation } from '~/Locale';
import { translateRoute } from '~/App/shared/localization';
import {
  sellflowErrorFallbackSolution,
  sellflowReset,
  sellflowSetRegistrationPlate,
  sellflowVehicleInfoDefaults,
  sellflowVehicleInfoSuccess
} from '~/App/shared/actions/sellflowActions';
import { handleGetLeadObjectError } from '~/App/shared/actions/sellFlowAsyncActions';
import { getLeadObject } from '~/helpers/orchestration/sale';

export interface UseRegistrationPlateSubmit {
  onSubmit: (plate: string) => void;
  isLoading: boolean;
  error: string | null;
}

export const useRegistrationPlateSubmit = (): UseRegistrationPlateSubmit => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchParams = new URLSearchParams(location.search);
  const campaignIdentifier = searchParams.get('campaign_identifier')
    ? `&campaign_identifier=${searchParams.get('campaign_identifier')}`
    : ``;

  const onSubmit = (plate: string) => {
    setLoading(true);
    setError(null);

    dispatch(sellflowReset());
    dispatch(sellflowSetRegistrationPlate(plate));

    getLeadObject({
      params: {
        registrationPlate: plate
      }
    })
      .then(res => {
        if (res.salesFlow) {
          dispatch(sellflowVehicleInfoSuccess(res));
          dispatch(sellflowVehicleInfoDefaults(res));

          const flow = res?.salesFlow;
          const route =
            flow === 'HEAVY_EQUIPMENT'
              ? `${translateRoute(
                  t,
                  '/ROUTES.SELL_HEAVY_EQUIPMENT'
                )}?r=${plate}${campaignIdentifier}`
              : `${translateRoute(
                  t,
                  '/ROUTES.SELL/ROUTES.SELL_CAR_INFO'
                )}?r=${plate}${campaignIdentifier}`;

          history.push(route);
        } else {
          dispatch(sellflowErrorFallbackSolution());
        }
      })
      .catch((error: AxiosError) => {
        const shouldUseFallbackSolution = handleGetLeadObjectError({
          error,
          dispatch,
          action: setError,
          t
        });
        shouldUseFallbackSolution &&
          history.push(
            `${translateRoute(
              t,
              '/ROUTES.SELL/ROUTES.SELL_CAR_INFO'
            )}?r=${plate}${campaignIdentifier}`
          );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return { onSubmit, isLoading, error };
};
